<template>
  <section>
    <scroll-container
      class="pt-2 mt-n30px"
      :height-offset="isInEditMode? '55px' : '125px'"
    >
      <b-row v-if="loading">
        <b-col
          sm="12"
          class="text-center"
        >
          <b-skeleton
            class="d-inline-block"
            animation="wave"
            width="40%"
            height="668px"
          />
        </b-col>
      </b-row>
      <b-container
        v-else
        class="container-small"
      >
        <b-card class="mb-2">
          <div class="mb-2">
            <b-card-title
              v-if="isInEditMode"
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.EditVideoOutstreamAd') }}
            </b-card-title>
            <b-card-title
              v-else
              class="mb-0"
            >
              {{ $tk('ClientPortal.Form.NewVideoOutstreamAd') }}
            </b-card-title>
            <b-breadcrumb class="m-0 p-0">
              <b-breadcrumb-item :to="`/${$route.params.accountId}/campaign/edit/${wrapper.campaignModel.id}`">
                {{ capaignName }}
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="`/${$route.params.accountId}/adgroup/edit?adGroupId=${wrapper.adGroup.id}&campaignId=${wrapper.campaignModel.id}`">
                {{ adGroupName }}
              </b-breadcrumb-item>
              <b-breadcrumb-item
                active
              >
                {{ isInEditMode ? displayAd.adName : $tk('ClientPortal.Form.NewAd') }}
              </b-breadcrumb-item>
            </b-breadcrumb>
            <hr class="m-0 mt-1">
          </div>
          <b-form-group
            class="mb-2"
            :invalid-feedback="displayAdErrors['FormatId']"
            :state="displayAdErrors['FormatId'] == null ? null : false"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.AdCreation.AdFormats') }} *
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.AdFormatsTooltip')"
                name="Info"
              />
            </legend>
            <v-select
              id="FormatId"
              v-model="displayAd.formatId"
              :clearable="false"
              label="name"
              class="smaller-indicator"
              :placeholder="$tk('ClientPortal.Form.ChooseFormat')"
              :options="wrapper.adFormats"
              :reduce="format => format.id"
            />
          </b-form-group>
          <b-form-group
            id="AdName"
            class="mb-2"
            :label="$tk('ClientPortal.Form.AdName') + ' *'"
            :invalid-feedback="displayAdErrors['AdName']"
          >
            <AdNameField
              :id="'ad-name-'+displayAd.id"
              v-model="displayAd.adName"
              :variables="adNameVariables"
              :placeholder="$tk('ClientPortal.Form.AdNamePlaceholder')"
              :state="displayAdErrors['AdName'] == null ? null : false"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :invalid-feedback="displayAdErrors['Url']"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.Url') }} *
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.URLTooltip')"
                name="Info"
              />
            </legend>
            <b-form-input
              id="Url"
              v-model="displayAd.url"
              type="text"
              :placeholder="$tk('ClientPortal.Form.UrlPlaceholder')"
              :state="displayAdErrors['Url'] == null ? null : false"
            />
          </b-form-group>
          <b-button
            v-b-toggle="'url-builder'"
            class="bg-white mb-2"
            size="sm"
            variant="outline-primary"
          >
            <GjIcon
              class="mr-50"
              name="Hyperlink"
            />
            <span class="when-open">
              {{ $tk('ClientFormat.Form.CloseUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowUp"
              />
            </span>
            <span class="when-closed">
              {{ $tk('ClientFormat.Form.OpenUrlBuilder') }}
              <GjIcon
                class="mr-50"
                name="ArrowDown"
              />
            </span>
          </b-button>
          <b-collapse
            :id="'url-builder'"
          >
            <url-builder
              :ref="'utm-'+displayAd.id"
              :key="displayAd.id"
              v-model="displayAd.url"
              :options="wrapper.utmOptions"
            />
          </b-collapse>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.ImpressionTag') }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.ImpressionTagTooltip')"
                name="Info"
              />
            </legend>
            <b-form-input
              v-model="displayAd.impressionTag"
              type="text"
              :placeholder="$tk('ClientPortal.Form.ImpressionTagPlaceholder')"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
          >
            <legend class="col-form-label pt-0">
              {{ $tk('ClientPortal.Form.Video')+ ' *' }}
              <GjIcon
                v-b-tooltip.hover="$tk('ClientPortal.Form.VideoTooltip')"
                name="Info"
              />
            </legend>
            <multiselect
              v-model="selectedVideo"
              :options="videos"
              :searchable="true"
              :show-labels="false"
              :option-height="50"
              :placeholder="$tk('ClientPortal.Form.SearchOrSelectVideo')"
              track-by="id"
              label="title"
              @focus.stop=""
            >
              <template
                slot="option"
                slot-scope="props"
              >
                <b-row>
                  <b-col sm="2">
                    <div
                      v-if="props.option.thumbnailUrl != null"
                      class="selected-item-thumbnail d-inline-block"
                      :style="{ backgroundImage: 'url(' + props.option.thumbnailUrl + ')' }"
                    />
                  </b-col>
                  <b-col
                    class=" d-flex"
                    sm="10"
                  >
                    <div class="selected-item-title d-inline-block align-self-center">
                      {{ props.option.title }}
                    </div>
                  </b-col>
                </b-row>
              </template>
              <template
                slot="caret"
                slot-scope="{ toggle }"
              >
                <div
                  class="multiselect__select"
                  @mousedown.prevent.stop="toggle()"
                >
                  <GjIcon
                    class="icon-wrapper"
                    name="ArrowDown_fill"
                    size="21"
                  />
                </div>
              </template>
            </multiselect>
          </b-form-group>
          <text-divider :text="$tk('ClientPortal.Form.Or')" />
          <div class="text-center mt-2">
            <b-button
              v-b-modal.upload-video-modal
              variant="outline-primary"
              size="sm"
            >
              <GjIcon
                name="Plus"
                class="mr-50"
              />
              {{ $tk('ClientPortal.Form.UploadAVideo') }}
            </b-button>
          </div>
        </b-card>

        <b-card
          class="mb-3"
          no-body
        >
          <b-card-title
            v-b-toggle.collapse-ad-preview
            class="d-flex justify-content-between p-2 mb-0"
            @click="showPreview(selectedVideo)"
          >
            <div
              class="text-primary"
            >
              <GjIcon name="Show" />
              {{ $tk('ClientPortal.Form.AdCreation.AdPreview') }}
            </div>
            <span class="when-open">
              <GjIcon name="ArrowUp" />
            </span>
            <span class="when-closed">
              <GjIcon name="ArrowDown" />
            </span>
          </b-card-title>
          <b-collapse
            id="collapse-ad-preview"
          >
            <b-card-body class="p-0">
              <hr class="mt-0 mb-0">
              <div
                class="card w-100"
              >
                <div
                  v-if="previewVideo"
                  class="card-body bg-white"
                >
                  <div
                    v-if="encoding"
                    class="mb-2 encoding-text"
                  >
                    <GjIcon
                      name="SettingsAlt"
                      size="18"
                    />
                    {{ $tk('ClientPortal.Form.AdCreation.VideoIsBeingEncodedPleaseWait') }}
                  </div>
                  <div v-if="previewVideo.mediaUrl">
                    <div class="video-ad">
                      <video
                        id="video"
                        ref="videoAdPreview"
                        width="100%"
                        controls
                      />
                    </div>
                  </div>
                </div>
                <span
                  v-else
                  class="encoding-text"
                >
                  {{ $tk('ClientPortal.Form.AdCreation.NoVideoHasBeenSelectedYet') }}
                </span>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </b-container>
    </scroll-container>
    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          v-if="isInEditMode"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
          @click="closeEditModal"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          v-else
          :to="`/${$route.params.accountId}`"
          class="bg-white"
          variant="outline-dark"
          size="sm"
          :disabled="saving"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click="saveVideoAd()"
        >
          {{ isInEditMode ? $tk('ClientPortal.Form.Actions.Save') : $tk('ClientPortal.Form.Actions.CreateAd') }}
        </b-button>
      </div>
    </div>
    <b-modal
      id="upload-video-modal"
      variant="primary"
      :title="$tk('ClientPortal.Form.UploadVideo')"
      :ok-title="$tk('ClientPortal.Form.Actions.Save')"
      centered
      :no-close-on-esc="isUploading && barVariant !== 'danger'"
      :no-close-on-backdrop="isUploading && barVariant !== 'danger'"
      :hide-header-close="isUploading && barVariant !== 'danger'"
      ok-only
      size="md"
      :ok-disabled="!videoEdit.videoId || barVariant === 'danger'"
      @ok="updateVideoName"
      @hidden="clearErrorMsg"
    >
      <b-row>
        <b-col sm="12">
          <input
            id="video-file-name"
            v-model="videoEdit.fileName"
            type="text"
            :placeholder="$tk('ClientPortal.Form.VideoFileNamePlaceHolder')"
            class="form-control form-control-sm mb-2"
          >
          <input
            id="video-file-id"
            v-model="videoEdit.videoId"
            type="hidden"
            class="form-control mb-2"
          >
          <input
            id="upload-account-id"
            type="hidden"
            :value="$route.params.accountId"
          >
          <vue-dropzone
            id="dropzone"
            ref="videoDropzone"
            :options="dropzoneOptions"
            :use-custom-slot="true"
            style="width:100%; text-align: center;"
            @vdropzone-file-added="passFile"
          >
            <div class="dropzone-custom-content">
              <b-button
                variant="outline-primary"
                size="sm"
                @click.stop="openVideoUpload"
              >
                <GjIcon
                  class="mr-50"
                  name="Upload"
                />
                {{ $t('ClientPortal.Form.UploadVideo') }}
              </b-button>
              <div
                width="100%"
                style="pointer-events:none;"
              >
                {{ $tk('ClientPortal.Form.Max8MBFileAllowed') }}
              </div>
            </div>
          </vue-dropzone>
          <input
            id="videoFile"
            ref="openUpload"
            hidden
            accept="video/*"
            type="file"
            name="video"
            :disabled="isUploading && barVariant !== 'danger'"
            @input="onFileSelected"
          >
          <div
            v-if="isUploading"
            class="d-flex flex-column w-100 col-12 p-0 pt-1"
          >
            <b-col
              md="12"
              class=" px-0"
            >
              <uploader-bar
                v-model="fileProgress"
                :variant="barVariant"
                :message="uploaderBarMessage"
              />
            </b-col>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12">
          <div
            id="progress-bar-holder"
            ref="progressBarHolder"
          />
        </b-col>
      </b-row>
    </b-modal>
  </section>
</template>

<script>
import ScrollContainer from '@/components/ScrollContainer.vue'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'
import UrlBuilder from '@/components/UrlBuilder.vue'
import TextDivider from '@/components/TextDivider.vue'
import AdsService from '@/api/client/ads.service'
import axiosIns from '@/libs/axios'
import Multiselect from 'vue-multiselect'
import UploaderBar from '@/components/UploaderBar.vue'
import VideoService from '@/api/client/video.service'
import sweetAlert from '@/mixins/sweetAlert'
import axios from 'axios'
import translation from '@/mixins/translate'
import vSelect from 'vue-select'
import Hls from 'hls.js'
import {
  ref, onMounted, computed, inject,
} from '@vue/composition-api'
import scrollToError from '@/mixins/scrollToError'
import vueDropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import AdNameField from '@/components/AdNameField.vue'
import { AD_NAME_VARIABLES, findUnknownTokens } from '@/utils/adNameTokens'

const ADSUBTYPE_VIDEO_OUTSTREAM = 11

export default {
  inject: ['closeEditModal'],
  components: {
    GjIcon,
    Multiselect,
    ScrollContainer,
    UrlBuilder,
    TextDivider,
    UploaderBar,
    vueDropzone,
    vSelect,
    AdNameField,
  },
  mixins: [notification, sweetAlert],
  props: {
    adId: Number,
    adGroupId: Number,
    catalogSetId: {
      type: String,
    },
    saving: {
      type: Boolean,
    },
  },
  setup(props, context) {
    const wrapper = ref({})
    const loading = ref(false)
    const { accountId } = context.root.$route.params
    const capaignName = ref('')
    const adGroupName = ref('')
    const displayAd = ref({})
    const displayAdErrors = ref({})
    const videos = ref([])
    const selectedVideo = ref({})
    const preview = ref(false)
    const previewVideo = ref(null)
    const videoEdit = ref({})
    const isUploading = ref(false)
    const barVariant = ref('primary')
    const isFileUploading = ref(false)
    const fileProgress = ref(0)
    const uploaderBarMessage = ref('null')
    const dropzoneOptions = {
      maxFiles: 1,
      parallelUploads: 1,
      uploadMultiple: false,
      acceptedFiles: '.mp4,.flv',
      url: 'url',
      thumbnailWidth: null,
      timeout: 0,
      thumbnailHeight: null,
      addRemoveLinks: true,
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    }
    const encoding = ref(false)
    const isInEditMode = computed(() => props.adId > 0)

    const isFormComplete = computed(() => {
      const {
        adName, url,
      } = displayAd.value

      return adName && url && selectedVideo.value
    })

    const closeModal = inject('closeEditModal')

    onMounted(async () => {
      loading.value = true
      wrapper.value = await AdsService.getAd(props.adId, props.adGroupId, props.catalogSetId, accountId)
      wrapper.value.displayAds = {
        id: 1,
        adName: '',
        mediaUrl: '',
        url: '',
        formatId: wrapper.value.adFormats[0].id,
        adGroupId: wrapper.value.adGroup.id,
        externalContent: '',
        impressionTag: '',
        show: false,
        errors: {},
        videoId: null,
      }
      videos.value = wrapper.value.videos
      capaignName.value = wrapper.value.campaignModel.name
      adGroupName.value = wrapper.value.adGroup.name
      displayAd.value = isInEditMode.value ? wrapper.value.displayAdEdit : wrapper.value.displayAds
      displayAdErrors.value = isInEditMode.value ? wrapper.value.displayAdEdit.errors : wrapper.value.displayAds.errors
      selectedVideo.value = videos.value.find(video => video.id === displayAd.value.videoId)
      scrollToError(displayAdErrors.value)
      loading.value = false
    })

    const loadVideo = url => {
      if (Hls.isSupported()) {
        const video = document.getElementById('video')
        if (url.endsWith('.mp4')) {
          video.src = url
          video.play()
        } else {
          const hls = new Hls()
          hls.loadSource(url)
          hls.attachMedia(video)
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play()
          })
        }
      }
    }

    const showPreview = async selectedVideoInner => {
      if (preview.value) {
        context.refs.videoAdPreview.pause()
        preview.value = false
      } else {
        encoding.value ? previewVideo.value = {} : previewVideo.value = null
        if (selectedVideoInner != null && selectedVideoInner.id > 0) {
          try {
            const response = await VideoService.getVideo(selectedVideoInner.id, accountId)
            previewVideo.value = response
            if (previewVideo.value.mediaUrl != null) {
              preview.value = true
              context.root.$nextTick(() => {
                loadVideo(previewVideo.value.mediaUrl)
                encoding.value = false
              })
            } else if (!encoding.value) {
              encoding.value = true
              notification.methods.error(previewVideo.value.message)
            }
          } catch (error) {
            notification.methods.error(error.message)
          }
        }
      }
    }

    const escapeImpressionTag = displayAds => {
      displayAds.forEach(element => {
        element.impressionTag = encodeURIComponent(element.impressionTag)
      })
    }

    const setSaving = (value, success = false) => {
      context.emit('onUpdate', value, success)
    }

    const adNameVariables = AD_NAME_VARIABLES

    const validateAdName = ad => {
      if (!ad.errors) ad.errors = {}
      ad.errors.AdName = null
      displayAdErrors.value = ad.errors
      const unknown = findUnknownTokens(ad.adName)
      if (unknown.length > 0) {
        const tokens = unknown.map(k => `{{${k}}}`).join(', ')
        ad.errors.AdName = `Unknown variables: ${tokens}`
        displayAdErrors.value = { ...ad.errors }
        return false
      }
      return true
    }

    const saveVideoAd = async () => {
      setSaving(true)
      displayAd.value.videoId = selectedVideo.value.id
      if (!validateAdName(displayAd.value)) {
        context.root.$nextTick(() => scrollToError(displayAdErrors.value))
        setSaving(false)
        return
      }
      if (isInEditMode.value) {
        const { entity } = context.parent.$parent.$parent
        escapeImpressionTag([displayAd.value])
        try {
          const response = await AdsService.editDisplayAd(displayAd.value, accountId)
          if (response.updated) {
            const resolvedName = response.adName || (response.displayAdModel && response.displayAdModel.adName) || displayAd.value.adName
            displayAd.value.adName = resolvedName
            entity.name = resolvedName
            notification.methods.success(response.message)
            closeModal()
          } else {
            notification.methods.error(response.message)
            displayAd.value = response.displayAdModel
            displayAdErrors.value = response.displayAdModel.errors
            context.root.$nextTick(() => {
              scrollToError(displayAdErrors.value)
            })
          }
        } catch (error) {
          notification.methods.error(error.message)
        } finally {
          setSaving(false)
        }
      } else {
        escapeImpressionTag([displayAd.value])
        try {
          const response = await AdsService.createDisplayAd(ADSUBTYPE_VIDEO_OUTSTREAM, [displayAd.value], accountId)
          if (response.saved) {
            sweetAlert.methods.sweetAlertAfterAdCreation(accountId)
            displayAdErrors.value = {}
            setSaving(false, true)
          } else {
            [displayAd.value] = response.displayAds
            displayAdErrors.value = response.displayAds[0].errors
            notification.methods.error(translation.methods.$tk('ClientPortal.Ads.ErrorSavingChanges'))
            context.root.$nextTick(() => {
              scrollToError(displayAdErrors.value)
            })
            setSaving(false)
          }
        } catch (error) {
          notification.methods.error(error.message)
          setSaving(false)
        }
      }
    }

    const openVideoUpload = () => {
      context.refs.openUpload.click()
    }
    const clearErrorMsg = () => {
      if (barVariant.value === 'danger' && isUploading.value) {
        isUploading.value = false
        barVariant.value = 'primary'
      }
    }
    const checkFileType = type => type.startsWith('video')
    const passFile = file => {
      context.refs.videoDropzone.removeFile(file)
      /* eslint-disable-next-line no-use-before-define */
      onFileSelected(file, true)
    }
    const onFileSelected = async (e, dragAndDrop = false) => {
      /* eslint-disable no-else-return, no-useless-return */
      if (isFileUploading.value) {
        return
      } else {
        isFileUploading.value = true
        isUploading.value = true
        fileProgress.value = 0
        barVariant.value = 'primary'
        let video
        if (dragAndDrop) {
          video = e
          if (!checkFileType(e.type)) {
            isFileUploading.value = false
            barVariant.value = 'danger'
            uploaderBarMessage.value = translation.methods.$tk('ClientPortal.Ads.VideoAd.UploadFailedWrongFileExtension')
            return
          }
        } else { /* eslint-disable-next-line */
          video = e.target.files[0]
          if (!checkFileType(e.target.files[0].type)) {
            isFileUploading.value = false
            barVariant.value = 'danger'
            uploaderBarMessage.value = translation.methods.$tk('ClientPortal.Ads.VideoAd.UploadFailedWrongFileExtension')
            return
          }
        }
        const params = {
          fileName: video.name,
          mimeType: video.type,
        }
        const headers = {
          'Access-Control-Allow-Origin': '*',
        }
        await axiosIns.post(`upload/presignedurl?accountId=${accountId}`, params, {
          headers,
        }).then(response => {
          const signedUrl = response.data.result.url
          const options = {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/octet-stream',
              'Passed-Req-Key': response.data.result.requestKey,
              'x-amz-acl': 'public-read',

            },
            onUploadProgress: progressEvent => {
              const { total } = progressEvent
              const totalLength = progressEvent.lengthComputable ? total : null
              if (totalLength !== null) {
                fileProgress.value = Math.round((progressEvent.loaded * 100) / total)
              }
            },
          }
          return axios.put(signedUrl, video, options)
            .then(async result => {
              const encodeHeader = {
                downloadUrl: result.config.headers['Passed-Req-Key'],
              }
              await axiosIns.post('upload/presignedurl/encode', encodeHeader)
                .then(res => {
                  videoEdit.value.fileName = res.data.result.name
                  videoEdit.value.videoId = res.data.result.id
                  isUploading.value = false
                  fileProgress.value = 0
                })
            })
            .catch(err => {
              if (err) {
                isUploading.value = true
                fileProgress.value = 100
                barVariant.value = 'danger'
              }
            })
        })
          .catch(err => {
            if (err) {
              isUploading.value = true
              fileProgress.value = 100
              barVariant.value = 'danger'
            }
          })
      }
      isFileUploading.value = false
    }
    const updateVideoName = async bvModalEvt => {
      bvModalEvt.preventDefault()
      try {
        const response = await VideoService.updateVideo(videoEdit.value.videoId, videoEdit.value.fileName, accountId)
        if (response.success) {
          bvModalEvt.vueTarget.hide('upload-video-modals')
          const exists = videos.value.filter(x => x.id === videoEdit.videoId)
          const data = {
            id: videoEdit.value.videoId,
            title: videoEdit.value.fileName,
            thumbnailUrl: null,
          }
          if (exists.length === 0) {
            videos.value.push(data)
            videoEdit.value.id = 0
            videoEdit.value.fileName = ''
          }
          selectedVideo.value = data
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
        }
      } catch (error) {
        notification.methods.error(error.message)
      }
    }

    return {
      wrapper,
      loading,
      isInEditMode,
      capaignName,
      adGroupName,
      displayAd,
      displayAdErrors,
      videos,
      selectedVideo,
      loadVideo,
      showPreview,
      uploaderBarMessage,
      dropzoneOptions,
      checkFileType,
      passFile,
      preview,
      previewVideo,
      isFormComplete,
      setSaving,
      saveVideoAd,
      videoEdit,
      openVideoUpload,
      isUploading,
      barVariant,
      fileProgress,
      onFileSelected,
      updateVideoName,
      clearErrorMsg,
      encoding,
      adNameVariables,
    }
  },
}
</script>
